/**
 * OriginalEmailRenderer — full-fidelity inbound-email section (Task 4054d).
 *
 * Renders the customer's original inbound email on `send_email` approval
 * cards so the operator can judge the proposed reply against the request
 * without leaving the card. Composes the shared inbox-manager primitives
 * (Task 3201b extraction) following the MessageView composition pattern:
 *
 *   sender/subject header → attachments bar (AttachmentChip per attachment)
 *   → body (HtmlEmailFrame when html_body present, plain-text otherwise)
 *   → AttachmentPreviewModal on chip click.
 *
 * HtmlEmailFrame carries Task 4053e's contrast/legibility pass, so
 * white-on-white email templates are corrected automatically. All card
 * chrome (header, chips) is rendered OUTSIDE the iframe document per the
 * 4053e handoff note, so the legibility pass never audits our chrome.
 *
 * The frame height is constrained so a long email scrolls within the
 * section instead of dominating the card layout.
 *
 * Data shape: `OriginalEmailData` from `buildSyntheticDetail.ts`. The
 * renderer validates defensively — if neither body field is a usable
 * string it renders a muted notice (the builder normally omits the
 * section entirely in that case, so this is belt-and-braces).
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Paperclip } from 'lucide-react'

import {
  HtmlEmailFrame,
  AttachmentChip,
  AttachmentPreviewModal,
} from '@/features/inbox-manager/components/MessageRenderers'
import type { MessageAttachment } from '@/features/inbox-manager/types'
import type { RendererProps } from './registry'
import { useCardTranslatedText, useCardShowingTranslation } from '../CardTranslation'

interface ParsedOriginalEmail {
  from: string | null
  fromName: string | null
  subject: string | null
  htmlBody: string | null
  plainTextBody: string | null
  attachments: MessageAttachment[]
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function asNonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null
}

function parseData(data: unknown): ParsedOriginalEmail | null {
  if (!isPlainObject(data)) return null

  const attachments: MessageAttachment[] = []
  if (Array.isArray(data.attachments)) {
    for (const entry of data.attachments) {
      if (!isPlainObject(entry)) continue
      const filename = asNonEmptyString(entry.filename)
      if (!filename) continue
      attachments.push({
        id: asNonEmptyString(entry.id) ?? filename,
        filename,
        content_type: typeof entry.content_type === 'string' ? entry.content_type : '',
        size: typeof entry.size === 'number' ? entry.size : -1,
        ...(asNonEmptyString(entry.url) ? { url: entry.url as string } : {}),
      })
    }
  }

  return {
    from: asNonEmptyString(data.from),
    fromName: asNonEmptyString(data.from_name),
    subject: asNonEmptyString(data.subject),
    htmlBody: asNonEmptyString(data.html_body),
    plainTextBody: asNonEmptyString(data.plain_text_body),
    attachments,
  }
}

export function OriginalEmailRenderer({ data }: RendererProps) {
  const [previewAttachment, setPreviewAttachment] =
    React.useState<MessageAttachment | null>(null)

  const parsed = React.useMemo(() => parseData(data), [data])

  if (!parsed || (!parsed.htmlBody && !parsed.plainTextBody)) {
    return <p style={styles.emptyNotice}>Original email content unavailable.</p>
  }

  const senderLine = parsed.fromName
    ? parsed.from
      ? `${parsed.fromName} <${parsed.from}>`
      : parsed.fromName
    : parsed.from

  return (
    <div style={styles.container}>
      {/* Sender / subject header — card chrome, outside the iframe */}
      {(senderLine || parsed.subject) && (
        <div style={styles.header}>
          {senderLine && (
            <div style={styles.headerRow}>
              <span style={styles.headerKey}>From</span>
              <span style={styles.headerValue}>{senderLine}</span>
            </div>
          )}
          {parsed.subject && (
            <div style={styles.headerRow}>
              <span style={styles.headerKey}>Subject</span>
              <div style={styles.subjectWrap}>
                <EmailSubject subject={parsed.subject} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attachments bar — chips with click-to-preview */}
      {parsed.attachments.length > 0 && (
        <div style={styles.attachmentsBar}>
          <div style={styles.attachmentsHeader}>
            <Paperclip size={13} style={styles.attachmentsIcon} aria-hidden="true" />
            <span style={styles.attachmentsLabel}>
              {parsed.attachments.length} attachment
              {parsed.attachments.length > 1 ? 's' : ''}
            </span>
          </div>
          <div style={styles.attachmentsList}>
            {parsed.attachments.map((att) => (
              <AttachmentChip
                key={att.id}
                attachment={att}
                onPreview={setPreviewAttachment}
              />
            ))}
          </div>
        </div>
      )}

      {/* Body — full-fidelity HTML via the shared sandboxed frame (with the
          4053e legibility pass), plain text fallback otherwise. Height is
          constrained so long emails scroll inside the section. When the card
          is flipped to English the body is shown as translated PLAIN TEXT —
          we never send HTML to the translate endpoint. */}
      <EmailBody
        htmlBody={parsed.htmlBody}
        plainTextBody={parsed.plainTextBody}
      />

      {/* Attachment preview modal */}
      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  )
}

/**
 * Subject line that participates in the card-level translate control. Registers
 * the (plain-text) subject and shows the original or translation accordingly.
 */
function EmailSubject({ subject }: { subject: string }) {
  const shown = useCardTranslatedText(subject)
  return <span style={{ ...styles.headerValue, ...styles.subjectValue }}>{shown}</span>
}

/**
 * Email body. The original view is full fidelity — sandboxed HTML frame when an
 * HTML body is present, plain text otherwise. When the card is flipped to
 * English we show the body as translated PLAIN TEXT: we register a plain-text
 * representation (the plain-text body when available, otherwise the HTML
 * stripped to text) and never send HTML to the translate endpoint.
 */
function EmailBody({
  htmlBody,
  plainTextBody,
}: {
  htmlBody: string | null
  plainTextBody: string | null
}) {
  const plainForTranslation = React.useMemo(
    () => plainTextBody ?? (htmlBody ? htmlToPlainText(htmlBody) : ''),
    [plainTextBody, htmlBody],
  )

  const shown = useCardTranslatedText(plainForTranslation)
  const showingTranslation = useCardShowingTranslation()

  // Show the translated plain text only when the card is flipped AND we have a
  // translation that actually differs from the source (i.e. it's loaded and the
  // body was non-English). Otherwise fall back to the full-fidelity view.
  const showTranslated =
    showingTranslation && plainForTranslation.length > 0 && shown !== plainForTranslation

  if (showTranslated) {
    return (
      <div style={styles.bodyContainer}>
        <div style={styles.plainTextBody}>{shown}</div>
      </div>
    )
  }

  return (
    <div style={styles.bodyContainer}>
      {htmlBody ? (
        <HtmlEmailFrame html={htmlBody} style={styles.emailFrame} />
      ) : (
        <div style={styles.plainTextBody}>{plainTextBody}</div>
      )}
    </div>
  )
}

/**
 * Best-effort HTML → plain text for translation. Uses the DOM to extract text
 * content (this runs in the Tauri webview), collapsing runs of blank lines.
 * Falls back to a crude tag strip if DOM parsing is unavailable.
 */
function htmlToPlainText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    // Remove non-content nodes BEFORE reading textContent. `textContent`
    // includes the text inside <style>/<script>, so without this the CSS rules
    // and JS source of an HTML email would be sent to the translate endpoint as
    // "text" — garbled, wasteful, and occasionally over the length cap. Email
    // templates routinely ship inline <style> blocks, so this is the common
    // case, not an edge case.
    doc
      .querySelectorAll('style, script, head, noscript, template')
      .forEach((el) => el.remove())
    const text = doc.body?.textContent ?? ''
    return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
  } catch {
    // DOM unavailable: crude strip. Drop <style>/<script> bodies first so their
    // contents don't survive the tag strip below.
    return html
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  emptyNotice: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    minWidth: 0,
  },
  headerKey: {
    flexShrink: 0,
    width: '52px',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  headerValue: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
    minWidth: 0,
  },
  subjectValue: {
    fontWeight: 600,
  },
  subjectWrap: {
    flex: 1,
    minWidth: 0,
  },
  attachmentsBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  attachmentsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  attachmentsIcon: {
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  attachmentsLabel: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  bodyContainer: {
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    overflow: 'hidden',
  },
  emailFrame: {
    // Constrain within the card — a long email scrolls inside the frame
    // instead of dominating the card layout (task spec, Step 2).
    minHeight: '160px',
    height: '320px',
    maxHeight: '40vh',
    borderRadius: 0,
  },
  plainTextBody: {
    margin: 0,
    padding: '12px 14px',
    fontSize: '13px',
    lineHeight: 1.6,
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '40vh',
    overflow: 'auto',
    background: 'var(--bg-tertiary)',
  },
}

export default OriginalEmailRenderer
